import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import ReactGA from 'react-ga4';
import Button from '@mui/material/Button';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Stack from '@mui/material/Stack';
import SeoHelmet from '../../common/SeoHelmet';
import Header from '../../common/Header';
import Footer from '../../common/Footer';

const SptcorrTheory = () => {
  const trackEvent = (action, label) => {
    try {
      ReactGA.event({
        category: 'SPT Theory',
        action,
        label,
      });
    } catch (err) {
      // no-op: analytics should never block interaction
    }
  };

  useEffect(() => {
    trackEvent('theory_page_view', 'spt-corr-theory');
  }, []);

  const handleCtaClick = (placement) => {
    trackEvent('calculator_cta_click', placement);
  };

  const alertDarkTextSx = {
    '& .MuiAlert-message': {
      color: (theme) => (theme.palette.mode === 'dark' ? 'var(--darkModeTextColor)' : 'inherit'),
    },
    '& .MuiAlertTitle-root': {
      color: (theme) => (theme.palette.mode === 'dark' ? 'var(--darkModeTextColor)' : 'inherit'),
    },
  };

  return (
    <>
      <SeoHelmet
        title="GeoCalc | SPT Correction | Theory"
        description="Explanation of SPT correction principles, including overburden, energy, borehole, sampler, and rod length adjustments used to obtain standardized (N₁)₆₀ values in geotechnical analysis."
        url="https://www.geocalc.app/spt-corr-theory"
        image="https://www.geocalc.app/assets/og-theory-v1.jpg"
        schemaType="page"
        breadcrumbs={[
          { name: 'Home', url: 'https://www.geocalc.app/' },
          { name: 'SPT Correction | Theory', url: 'https://www.geocalc.app/spt-corr-theory' },
        ]}
      />

      <Header />
      <div className="theory">
        <div className="theoryContainer">
          <h1>SPT Correction | Theory & Formulations</h1>
          <Alert
            severity="info"
            variant="outlined"
            sx={{
              ...alertDarkTextSx,
              mb: 2,
              '& .MuiAlert-message': {
                ...alertDarkTextSx['& .MuiAlert-message'],
                lineHeight: 1.85,
                width: '100%',
              },
              '& .correctionFactorTable': { width: '100%' },
              '& .correctionFactorTableRow': { justifyContent: 'stretch' },
            }}
          >
            <AlertTitle sx={{ fontWeight: 700 }}>What is SPT (Standard Penetration Test) ?</AlertTitle>
            The Standard Penetration Test (SPT) is a widely used in-situ soil testing method to determine the strength and
            consistency of soil at a particular location. It is an empirical test that involves driving a standard sampler with a
            weight of 65 kg and falling freely from a height of 760 mm into the soil for a total of 150 mm increments. The number
            of blows required to drive the sampler the last 300 mm of penetration is called the "N-value" or "blow count." <br />
            <br />
            The SPT is used to determine the soil type and properties, such as the density, strength, and shear resistance. The
            test is commonly used in geotechnical and foundation engineering to assess the suitability of a site for construction,
            design of foundations, and slope stability analysis. It is also used to estimate the liquefaction potential of soils
            during earthquakes, as well as for soil profiling and characterization in environmental and geological studies.
            <span style={{ width: '100%', display: 'flex', justifyContent: 'center', margin: '10px 0' }}>
              <Link to={'/calculations/spt-corr'} onClick={() => handleCtaClick('info_box_middle')}>
                <Button variant="outlined" size="small">
                  GO TO SPT CORRECTION CALCULATOR
                </Button>
              </Link>
            </span>
          </Alert>
          <Alert
            severity="info"
            variant="outlined"
            sx={{
              ...alertDarkTextSx,
              mb: 2,
              '& .MuiAlert-message': { ...alertDarkTextSx['& .MuiAlert-message'], lineHeight: 1.85 },
            }}
          >
            <AlertTitle sx={{ fontWeight: 700 }}>Why do we need SPT corrections?</AlertTitle>
            SPT (Standard Penetration Test) values often need to be corrected to account for various factors that can affect the
            test results. It's important to note that the correction factors can vary depending on the soil type, and other
            site-specific factors. Therefore, it's essential to consult with a geotechnical engineer to determine the appropriate
            correction factor for a specific site. Some of the reasons why SPT values need to be corrected are:
            <Stack spacing={2} sx={{ mt: 1.5 }}>
              <Alert
                severity="success"
                variant="outlined"
                sx={{ ...alertDarkTextSx, '& .MuiAlert-message': { ...alertDarkTextSx['& .MuiAlert-message'], width: '100%' } }}
              >
                <AlertTitle sx={{ fontWeight: 700 }}>Overburden stress</AlertTitle>
                The weight of the soil above the test location can cause stress in the soil and affect the penetration resistance
                of the sampler. The correction factor for overburden stress takes into account the effective overburden stress of
                the soil.
              </Alert>
              <Alert
                severity="success"
                variant="outlined"
                sx={{ ...alertDarkTextSx, '& .MuiAlert-message': { ...alertDarkTextSx['& .MuiAlert-message'], width: '100%' } }}
              >
                <AlertTitle sx={{ fontWeight: 700 }}>Sampler size and shape</AlertTitle>
                The size and shape of the sampler can affect the SPT values. The correction factor for sampler size and shape
                takes into account the area of the sampler and its shape factor.
              </Alert>
              <Alert
                severity="success"
                variant="outlined"
                sx={{ ...alertDarkTextSx, '& .MuiAlert-message': { ...alertDarkTextSx['& .MuiAlert-message'], width: '100%' } }}
              >
                <AlertTitle sx={{ fontWeight: 700 }}>Energy transfer</AlertTitle>
                The energy transferred from the hammer to the sampler can affect the SPT values. The correction factor for energy
                transfer takes into account the weight of the hammer, the height of fall, and the efficiency of the system.
              </Alert>
              <Alert
                severity="success"
                variant="outlined"
                sx={{ ...alertDarkTextSx, '& .MuiAlert-message': { ...alertDarkTextSx['& .MuiAlert-message'], width: '100%' } }}
              >
                <AlertTitle sx={{ fontWeight: 700 }}>Soil disturbance</AlertTitle>
                The SPT test can cause soil disturbance, which can affect the test results. The correction factor for soil
                disturbance takes into account the length of the sampler and the disturbance caused by the test.
              </Alert>
              <Alert
                severity="success"
                variant="outlined"
                sx={{ ...alertDarkTextSx, '& .MuiAlert-message': { ...alertDarkTextSx['& .MuiAlert-message'], width: '100%' } }}
              >
                <AlertTitle sx={{ fontWeight: 700 }}>Borehole diameter:</AlertTitle>
                The diameter of the borehole can affect the SPT values. The correction factor for borehole diameter takes into
                account the difference between the borehole diameter and the sampler diameter.
              </Alert>
            </Stack>
          </Alert>
          <Alert
            severity="info"
            variant="outlined"
            sx={{
              ...alertDarkTextSx,
              mb: 2,
              '& .MuiAlert-message': { ...alertDarkTextSx['& .MuiAlert-message'], lineHeight: 1.85 },
            }}
          >
            <AlertTitle sx={{ fontWeight: 700 }}>How do we perform SPT corrections ?</AlertTitle>
            Measured SPT values in the field (N) at each depth (z) will be corrected to (N<sub>1</sub>)<sub>60</sub> values using
            the equation below.
            <p className="theoryEquation">
              (N<sub>1</sub>)<sub>60</sub> = N' x C<sub>E</sub> x C<sub>N</sub> x C<sub>B</sub> x C<sub>S</sub> x C<sub>R</sub> x
              C<sub>BF</sub>
            </p>
            <p className="theoryEquation">Where;</p>
            <Stack spacing={2} sx={{ mt: 1.5 }}>
              <Alert severity="success" variant="outlined" sx={alertDarkTextSx}>
                <AlertTitle sx={{ fontWeight: 700 }}>
                  N': SPT value after silty sand correction (dilatancy correction) (Terzaghi & Peck, 1948)
                </AlertTitle>
                If the below 3 conditions are present, silty sand correction should be made. N is the value measured in the field.
                <p>1. If the ground water level is above test depth.</p>
                <p>2. If the soil is sand or silty sand</p>
                <p>3. If N &gt;15 </p>
                <p>If all the conditions are met, N' is calculated using the equation below.</p>
                <p className="theoryEquation">N' = 15 + 0.5(N - 15)</p>
              </Alert>
              <Alert severity="success" variant="outlined" sx={alertDarkTextSx}>
                <AlertTitle sx={{ fontWeight: 700 }}>
                  C<sub>E</sub>: Energy correction factor (Skempton, 1986)
                </AlertTitle>
                <p>Energy correction factor will be selected from the table below.</p>
                <div className="correctionFactorTable">
                  <div className="correctionFactorTableRow">
                    <div className="correctionFactorTableColumn">Hammer Type</div>
                    <div className="correctionFactorTableColumn">
                      <p>
                        C<sub>E</sub>
                      </p>
                    </div>
                  </div>
                  <div className="correctionFactorTableRow">
                    <div className="correctionFactorTableColumn">Safety</div>
                    <div className="correctionFactorTableColumn">0.60 - 1.17</div>
                  </div>
                  <div className="correctionFactorTableRow">
                    <div className="correctionFactorTableColumn">Donut</div>
                    <div className="correctionFactorTableColumn">0.45 - 1.00</div>
                  </div>
                  <div className="correctionFactorTableRow">
                    <div className="correctionFactorTableColumn no-border-bottom">Auto</div>
                    <div className="correctionFactorTableColumn no-border-bottom">0.90 - 1.60</div>
                  </div>
                </div>
              </Alert>
              <Alert severity="success" variant="outlined" sx={alertDarkTextSx}>
                <AlertTitle sx={{ fontWeight: 700 }}>
                  C<sub>N</sub>: Overburden Pressure Correction Factor (Liao & Whitman, 1986)
                </AlertTitle>
                <p>
                  Overburden correction factor will be calculated using the equation below. The vertical effective pressure,
                  &sigma;'(kN/m2) should be calculated for the ground conditions when the Standard Penetration Test (SPT) is
                  performed. Any excavation, fill or foundation loads after the test will be ignored.
                </p>
                <p className="theoryEquation">
                  C<sub>N</sub> = 9.78 x (1 / &sigma;') <sup>0.5</sup> &le; 1.70
                </p>
              </Alert>
              <Alert severity="success" variant="outlined" sx={alertDarkTextSx}>
                <AlertTitle sx={{ fontWeight: 700 }}>
                  C<sub>B</sub>: Borehole Diameter Correction Factor (Skempton, 1986)
                </AlertTitle>
                <p>Borehole diameter correction factor will be selected from the table below.</p>
                <div className="correctionFactorTable">
                  <div className="correctionFactorTableRow">
                    <div className="correctionFactorTableColumn">Borehole Dia.(mm)</div>
                    <div className="correctionFactorTableColumn">
                      <p>
                        C<sub>B</sub>
                      </p>
                    </div>
                  </div>
                  <div className="correctionFactorTableRow">
                    <div className="correctionFactorTableColumn">65 - 115</div>
                    <div className="correctionFactorTableColumn">1.00</div>
                  </div>
                  <div className="correctionFactorTableRow">
                    <div className="correctionFactorTableColumn">150</div>
                    <div className="correctionFactorTableColumn">1.05</div>
                  </div>
                  <div className="correctionFactorTableRow">
                    <div className="correctionFactorTableColumn no-border-bottom">200</div>
                    <div className="correctionFactorTableColumn no-border-bottom">1.15</div>
                  </div>
                </div>
              </Alert>
              <Alert severity="success" variant="outlined" sx={alertDarkTextSx}>
                <AlertTitle sx={{ fontWeight: 700 }}>
                  C<sub>S</sub>: Sampler Type Correction Factor (Skempton, 1986)
                </AlertTitle>
                <p>Sampler type correction factor will be selected from the table below.</p>
                <div className="correctionFactorTable">
                  <div className="correctionFactorTableRow">
                    <div className="correctionFactorTableColumn">Sampler Type</div>
                    <div className="correctionFactorTableColumn">
                      <p>
                        C<sub>S</sub>
                      </p>
                    </div>
                  </div>
                  <div className="correctionFactorTableRow">
                    <div className="correctionFactorTableColumn">Standard Liner (With liner)</div>
                    <div className="correctionFactorTableColumn">1.00</div>
                  </div>
                  <div className="correctionFactorTableRow">
                    <div className="correctionFactorTableColumn no-border-bottom">Without Liner</div>
                    <div className="correctionFactorTableColumn no-border-bottom">1.10 - 1.30</div>
                  </div>
                </div>
              </Alert>
              <Alert severity="success" variant="outlined" sx={alertDarkTextSx}>
                <AlertTitle sx={{ fontWeight: 700 }}>
                  C<sub>R</sub>: Rod Length Correction Factor (Skempton, 1986)
                </AlertTitle>
                <p>Rod length correction factor will be selected from the table below.</p>
                <div className="correctionFactorTable">
                  <div className="correctionFactorTableRow">
                    <div className="correctionFactorTableColumn">Rod Length(m)</div>
                    <div className="correctionFactorTableColumn">
                      <p>
                        C<sub>R</sub>
                      </p>
                    </div>
                  </div>
                  <div className="correctionFactorTableRow">
                    <div className="correctionFactorTableColumn">0 - 4</div>
                    <div className="correctionFactorTableColumn">0.75</div>
                  </div>
                  <div className="correctionFactorTableRow">
                    <div className="correctionFactorTableColumn">4 - 6</div>
                    <div className="correctionFactorTableColumn">0.85</div>
                  </div>
                  <div className="correctionFactorTableRow">
                    <div className="correctionFactorTableColumn">6 - 10</div>
                    <div className="correctionFactorTableColumn">0.95</div>
                  </div>
                  <div className="correctionFactorTableRow">
                    <div className="correctionFactorTableColumn no-border-bottom">&gt; 10</div>
                    <div className="correctionFactorTableColumn no-border-bottom">1.00</div>
                  </div>
                </div>
              </Alert>
              <Alert severity="success" variant="outlined" sx={alertDarkTextSx}>
                <AlertTitle sx={{ fontWeight: 700 }}>
                  C<sub>BF</sub>: Blow count frequency correction factor (McGregor ve Duncan, 1998)
                </AlertTitle>
                <p>Blow count frequency correction factor will be selected from the table below.</p>
                <div className="correctionFactorTable">
                  <div className="correctionFactorTableRow">
                    <div className="correctionFactorTableColumn">Blow Count</div>
                    <div className="correctionFactorTableColumn">
                      <p>
                        C<sub>BF</sub>
                      </p>
                    </div>
                  </div>
                  <div className="correctionFactorTableRow">
                    <div className="correctionFactorTableColumn">&lt; 20</div>
                    <div className="correctionFactorTableColumn">0.95</div>
                  </div>
                  <div className="correctionFactorTableRow">
                    <div className="correctionFactorTableColumn no-border-bottom">&gt; 20</div>
                    <div className="correctionFactorTableColumn no-border-bottom">1.05</div>
                  </div>
                </div>
              </Alert>
            </Stack>
          </Alert>
          <div className="theoryButtonContainer">
            <Link to={'/calculations/spt-corr'} onClick={() => handleCtaClick('bottom')}>
              <Button variant="outlined" size="small">
                GO TO SPT CORRECTION CALCULATOR
              </Button>
            </Link>
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default SptcorrTheory;
